import useSWR, { Key, SWRResponse, MutatorOptions } from 'swr';
import { AxiosResponse, AxiosError } from 'axios';
import axiosServices from 'utils/axios';
import { BASE_URL } from 'config';

export const consultantEndpoints = {
  profile: '/auth2/consultants/me',
  selectCity: '/auth2/consultants/select-city',
  userInfo: '/api/user-info'
};

interface ConsultantProfileData {
  id: string;
  name: string;
  logo?: string;
  logoUrl?: string;
  selectedCityId?: number | string | null;
}

interface ProfileResponse {
  consultant: ConsultantProfileData;
}

type SWRData = ProfileResponse | ConsultantProfileData | any; // Fallback to any for generic structure


const swrFetcher = async (url: string): Promise<SWRData> => {
  const response: AxiosResponse<SWRData> = await axiosServices.get(url);
  return response.data;
};

const resolveAxiosError = (error: any): string => {
  if (!error) return 'Unexpected error';
  const axiosError = error as AxiosError;
  const message =
    (axiosError.response?.data as any)?.message || (axiosError.response?.data as any)?.error || axiosError.message || 'Unexpected error';
  return message;
};


const normalizeConsultantProfile = (data: SWRData | null | undefined): ConsultantProfileData | null => {
  if (!data) {
    return null;
  }

  if (data && typeof data === 'object') {
    if (data.consultant && typeof data.consultant === 'object') {
      return data.consultant as ConsultantProfileData;
    }
  }

  return data as ConsultantProfileData;
};

const withAbsoluteLogo = (consultant: ConsultantProfileData | null): ConsultantProfileData | null => {
  if (!consultant) {
    return consultant;
  }

  const logo = consultant.logo || consultant.logoUrl;
  if (logo && typeof logo === 'string' && !/^https?:/i.test(logo)) {
    const base = BASE_URL?.replace(/\/$/, '') || '';
    const path = logo.startsWith('/') ? logo : `/${logo}`;
    return {
      ...consultant,
      logo: `${base}${path}`
    };
  }

  return consultant;
};


interface UseConsultantProfileOptions {
  enabled?: boolean;
}

interface UseConsultantProfileResult {
  consultant: ConsultantProfileData | null;
  consultantRaw: SWRData | null | undefined;
  consultantError: any;
  consultantLoading: boolean;
  mutateConsultant: (data?: SWRData | Promise<SWRData> | undefined, opts?: MutatorOptions<SWRData>) => Promise<SWRData | undefined>;
}

export const useConsultantProfile = (options: UseConsultantProfileOptions = {}): UseConsultantProfileResult => {
  const { enabled = true } = options || {};
  const swrKey: Key = enabled ? consultantEndpoints.profile : null;

  const { data, error, isLoading, mutate }: SWRResponse<SWRData> = useSWR(swrKey, swrFetcher, {
    revalidateOnFocus: false
  });

  const normalized = normalizeConsultantProfile(data);

  return {
    consultant: withAbsoluteLogo(normalized),
    consultantRaw: data,
    consultantError: error,
    consultantLoading: Boolean(enabled) && Boolean(isLoading),
    mutateConsultant: mutate
  };
};

export const selectConsultantCity = async (cityId: number | string): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosServices.post(consultantEndpoints.selectCity, { cityId });
    return response.data;
  } catch (error) {
    throw new Error(resolveAxiosError(error));
  }
};

export const fetchAuthenticatedUserInfo = async (): Promise<any> => {
  try {
    const response: AxiosResponse<any> = await axiosServices.get(consultantEndpoints.userInfo);
    return response.data;
  } catch (error) {
    throw new Error(resolveAxiosError(error));
  }
};

export const consultantApi = {
  useConsultantProfile,
  selectConsultantCity,
  fetchAuthenticatedUserInfo
};
